import { useEffect, useRef, useState, type MutableRefObject } from 'react';

import { useStateMachine } from '@heidi/flow/react';

import { getCurrentUser } from '@/hooks/query/useQueryUser';
import type { AuthState } from '@/lib/auth';
import { getUser } from '@/lib/utils';
import { chronicleBridge } from '@/tauri/chronicle';
import { autoSessionBridge } from '@/tauri/chronicle/auto-session';
import { bulkSyncBridge } from '@/tauri/chronicle/bulk-sync';
import {
  listenOfflineAutomaticRedriveReady,
  offlineSyncBridge,
} from '@/tauri/offline-recording';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';

import {
  chronicleBulkSyncStartupMachine,
  type ChronicleBulkSyncStartupInput,
} from '../chronicle-bulk-sync-startup.machine';

interface UseChronicleBulkSyncStartupArgs {
  bulkSyncAuth: { token: string; userId: string } | null;
  /** The raw auth cache state -- see the
   *  `!bulkSyncAuth` branch below, which treats a still-resolving cache
   *  differently from a settled logged-out state. */
  authState: AuthState;
  deviceSerial: string | null;
  getBaseUrl: () => string;
  runRecoveryDrain: (trigger: 'startup') => Promise<void>;
  initialAuthProvisionedRef: MutableRefObject<boolean>;
  clearAuthPromiseRef: MutableRefObject<Promise<void> | null>;
  rustAuth0TokenRefresh: boolean;
}

function isSessionAlreadyRunningError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { error_code?: unknown }).error_code === 'SESSION_ALREADY_RUNNING'
  );
}

type TokenProvision = {
  token: string;
  userId: string;
  /** Account epoch this provision was made under. */
  epoch: number;
  promise: Promise<void>;
  sideEffectsApplied: boolean;
};

type AutoSyncAuth = { token: string; userId: string; deviceSerial: string | null };

export const __resetChronicleBulkSyncStartupStateForTests = (): void => {
  // Nothing module-scoped survives a mount any more; kept so existing
  // suites can keep calling it.
};

/**
 * Non-reversible 32-bit fingerprint (FNV-1a) of the access token. Used only to
 * detect a token change for automatic re-drive dedup, so the raw bearer token
 * is never embedded in the retained dedup key — no extra credential copy
 * lingers in the heap.
 */
function fingerprintToken(token: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < token.length; i += 1) {
    hash ^= token.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16);
}

/**
 * Runs the explicit Chronicle startup handshake and owns its per-mount
 * single-flight guards. The pure transition graph lives in the adjacent
 * `chronicle-bulk-sync-startup.machine.ts` module.
 */
export function useChronicleBulkSyncStartup({
  bulkSyncAuth,
  authState,
  deviceSerial,
  getBaseUrl,
  runRecoveryDrain,
  initialAuthProvisionedRef,
  clearAuthPromiseRef,
  rustAuth0TokenRefresh,
}: UseChronicleBulkSyncStartupArgs): void {
  const latestDeviceSerialRef = useRef(deviceSerial);
  useEffect(() => {
    latestDeviceSerialRef.current = deviceSerial;
  }, [deviceSerial]);

  const engineStartupRef = useRef<{
    userId: string;
    promise: Promise<void>;
  } | null>(null);
  const tokenProvisionRef = useRef<TokenProvision | null>(null);
  const autoSyncAuthRef = useRef<AutoSyncAuth | null>(null);
  // Only the current token's key is needed for dedup, so hold a single
  // last-requested key rather than an ever-growing Set. The key stores a
  // non-reversible token fingerprint (not the raw token), so it keeps memory
  // bounded across a long-lived session without retaining a credential copy.
  const automaticRedriveRequestedRef = useRef<string | null>(null);
  const latestReadyEventGenerationRef = useRef<number | null>(null);
  const [automaticReadyGeneration, setAutomaticReadyGeneration] = useState<number | null>(
    null
  );
  const [automaticReadinessHydrated, setAutomaticReadinessHydrated] = useState(false);

  useEffect(() => {
    if (!isTauri()) return;

    let cancelled = false;
    let unlisten: (() => void) | undefined;
    void (async () => {
      let registeredUnlisten: () => void;
      try {
        registeredUnlisten = await listenOfflineAutomaticRedriveReady((event) => {
          if (!cancelled) {
            latestReadyEventGenerationRef.current = event.payload.targetGeneration;
            setAutomaticReadyGeneration(event.payload.targetGeneration);
          }
        });
      } catch (error) {
        logger.warn({
          msg: '[offline_recording::sync] Failed to subscribe to automatic re-drive readiness',
          error: error instanceof Error ? error.message : String(error),
        });
        if (!cancelled) setAutomaticReadinessHydrated(true);
        return;
      }

      if (cancelled) {
        registeredUnlisten();
        return;
      }
      unlisten = registeredUnlisten;

      // Subscribe before this authoritative snapshot so a ready edge cannot
      // fall into the gap between the initial machine read and async listener
      // registration. A concurrent event and snapshot are harmless: the
      // generation state and request guard both de-duplicate equivalents.
      try {
        const readiness = await offlineSyncBridge.getAutomaticRedriveReadiness();
        if (cancelled) {
          return;
        }
        setAutomaticReadyGeneration(
          latestReadyEventGenerationRef.current ?? readiness?.targetGeneration ?? null
        );
      } catch (error) {
        logger.warn({
          msg: '[offline_recording::sync] Failed to confirm automatic re-drive readiness',
          error: error instanceof Error ? error.message : String(error),
        });
      } finally {
        if (!cancelled) setAutomaticReadinessHydrated(true);
      }
    })();

    return () => {
      cancelled = true;
      unlisten?.();
    };
  }, []);

  const [startupState, sendStartup] = useStateMachine(chronicleBulkSyncStartupMachine, {
    services: {
      readAutomaticReadiness: (context) => {
        const hintedGeneration = context.input?.automaticTargetGeneration;
        if (hintedGeneration !== null && hintedGeneration !== undefined) {
          return { targetGeneration: hintedGeneration };
        }
        return null;
      },
      startEngine: async (context, { signal }) => {
        const input = context.input;
        if (!input) throw new Error('Chronicle startup input missing');

        await clearAuthPromiseRef.current;
        if (signal.aborted) return;

        let startup = engineStartupRef.current;
        if (!startup || startup.userId !== input.userId) {
          const promise = (async () => {
            try {
              await bulkSyncBridge.startEngine(input.userId);
              logger.info({ msg: '[bulk_sync] Session engine started' });
            } catch (error) {
              if (!isSessionAlreadyRunningError(error)) throw error;
              logger.info({ msg: '[bulk_sync] Session engine already running' });
            }
          })();
          startup = { userId: input.userId, promise };
          engineStartupRef.current = startup;
        }

        try {
          await startup.promise;
        } catch (error) {
          if (engineStartupRef.current === startup) {
            engineStartupRef.current = null;
          }
          logger.error({
            msg: '[bulk_sync] Startup initialization failed',
            error: error instanceof Error ? error.message : String(error),
          });
          throw error;
        }
      },
      syncBindingCode: async (_context, { signal }) => {
        if (signal.aborted) return;
        try {
          const serverBindingCode =
            getCurrentUser()?.hardware_device_binding_code ??
            (await getUser())?.data?.hardware_device_binding_code;
          if (serverBindingCode && !signal.aborted) {
            await chronicleBridge.setBindingCode(serverBindingCode);
            logger.info({ msg: '[bulk_sync] Binding code synced to the local store' });
          }
        } catch (error) {
          logger.warn({
            msg: '[bulk_sync] Failed to sync binding code to the local store',
            error: error instanceof Error ? error.message : String(error),
          });
        }
      },
      provisionToken: async (context, { signal }) => {
        const input = context.input;
        if (!input) throw new Error('Chronicle startup input missing');
        const baseUrl = getBaseUrl();

        let provision = tokenProvisionRef.current;
        if (
          !provision ||
          provision.userId !== input.userId ||
          provision.token !== input.token
        ) {
          const epoch = await bulkSyncBridge.getAccountEpoch();
          if (signal.aborted) return;

          provision = {
            token: input.token,
            userId: input.userId,
            epoch,
            promise: bulkSyncBridge.provideToken(
              input.token,
              baseUrl,
              epoch,
              rustAuth0TokenRefresh
            ),
            sideEffectsApplied: false,
          };
          tokenProvisionRef.current = provision;
        }

        try {
          await provision.promise;
        } catch (error) {
          logger.error({
            msg: '[bulk_sync] Startup initialization failed',
            error: error instanceof Error ? error.message : String(error),
          });
          throw error;
        }
        if (signal.aborted || provision.sideEffectsApplied) return;

        provision.sideEffectsApplied = true;
        const provisionedDeviceSerial = latestDeviceSerialRef.current;
        void autoSessionBridge.provideAuth(input.token, baseUrl, provision.epoch);
        bulkSyncBridge
          .provideAutoSyncAuth(
            input.token,
            baseUrl,
            input.userId,
            provision.epoch,
            provisionedDeviceSerial
          )
          .catch((error: unknown) => {
            logger.error({
              msg: '[bulk_sync] Failed to provide auto-sync auth',
              error: error instanceof Error ? error.message : String(error),
            });
          });
        autoSyncAuthRef.current = {
          token: input.token,
          userId: input.userId,
          deviceSerial: provisionedDeviceSerial,
        };
        initialAuthProvisionedRef.current = true;
        logger.info({ msg: '[bulk_sync] Initial token provided' });
      },

      requestAutomaticRedrive: async (context, { signal }) => {
        const input = context.input;
        if (!input || signal.aborted) return;

        const generation = context.automaticTargetGeneration;
        if (generation !== null) {
          // Token return/refresh is a new authenticated readiness lifecycle,
          // even when the native target generation is unchanged. Dedup on a
          // token fingerprint rather than the raw token so no credential copy
          // is retained in the key.
          const requestKey = `${input.userId}:${generation}:${fingerprintToken(input.token)}`;
          if (automaticRedriveRequestedRef.current !== requestKey) {
            automaticRedriveRequestedRef.current = requestKey;
            try {
              const outcome = await offlineSyncBridge.redriveAutomatically(
                input.userId,
                generation
              );
              logger.info({
                msg: '[offline_recording::sync] Automatic re-drive completed',
                outcome,
                targetGeneration: generation,
              });
            } catch (error) {
              logger.warn({
                msg: '[offline_recording::sync] Automatic re-drive IPC failed',
                error: error instanceof Error ? error.message : String(error),
              });
            }
          }
        }

        if (!signal.aborted) void runRecoveryDrain('startup');
      },
    },
  });

  useEffect(() => {
    if (!isTauri()) return;
    if (!automaticReadinessHydrated) return;

    if (!bulkSyncAuth) {
      engineStartupRef.current = null;
      tokenProvisionRef.current = null;
      autoSyncAuthRef.current = null;
      automaticRedriveRequestedRef.current = null;
      initialAuthProvisionedRef.current = false;
      if (!startupState.matches('idle')) sendStartup('RESET');
      return;
    }

    const nextInput: ChronicleBulkSyncStartupInput = {
      ...bulkSyncAuth,
      deviceSerial,
      automaticTargetGeneration: automaticReadyGeneration,
    };
    const activeInput = startupState.context.input;
    if (startupState.matches('idle')) {
      sendStartup({ type: 'START', input: nextInput });
      return;
    }

    if (activeInput) {
      const authChanged =
        activeInput.userId !== nextInput.userId || activeInput.token !== nextInput.token;
      const generationChanged =
        activeInput.automaticTargetGeneration !== nextInput.automaticTargetGeneration;
      if (!authChanged && !generationChanged) return;

      // A generation-only restart reuses the already-provisioned token. Keep
      // this shared readiness flag true so the associated recovery drain is
      // not accidentally suppressed.
      if (authChanged) initialAuthProvisionedRef.current = false;
      sendStartup('RESET');
      sendStartup({ type: 'START', input: nextInput });
    }
  }, [
    automaticReadyGeneration,
    automaticReadinessHydrated,
    bulkSyncAuth,
    authState,
    deviceSerial,
    initialAuthProvisionedRef,
    sendStartup,
    startupState,
    rustAuth0TokenRefresh,
  ]);

  // Device identity is not part of the critical engine/token handshake. Keep
  // the existing auto-sync device refresh, but only after this exact token has
  // completed the ordered provisioning flow.
  useEffect(() => {
    const provision = tokenProvisionRef.current;
    if (
      !isTauri() ||
      !bulkSyncAuth ||
      !initialAuthProvisionedRef.current ||
      !provision ||
      provision.token !== bulkSyncAuth.token ||
      provision.userId !== bulkSyncAuth.userId
    ) {
      return;
    }

    const previous = autoSyncAuthRef.current;
    if (
      previous?.token === bulkSyncAuth.token &&
      previous.userId === bulkSyncAuth.userId &&
      previous.deviceSerial === deviceSerial
    ) {
      return;
    }

    autoSyncAuthRef.current = { ...bulkSyncAuth, deviceSerial };
    bulkSyncBridge
      .provideAutoSyncAuth(
        bulkSyncAuth.token,
        getBaseUrl(),
        bulkSyncAuth.userId,
        provision.epoch,
        deviceSerial
      )
      .catch((error: unknown) => {
        logger.error({
          msg: '[bulk_sync] Failed to provide auto-sync auth on device-serial refresh',
          error: error instanceof Error ? error.message : String(error),
        });
      });
  }, [bulkSyncAuth, deviceSerial, getBaseUrl, initialAuthProvisionedRef]);
}
